import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { formatNumber, getSortIndicator } from '../../../../utils/helpers';
import useSortedData from '../../hooks/useSortedData';
import './EntrantsTeamsGolfCompleted.css'; // Import Completed CSS

const EntrantsTeamsGolfCompleted = ({ contest, currentUsername, onTeamSelect }) => {
  // Player data map is still needed for calculating team score
  const playerDataMap = useMemo(() => {
    const map = {};
    contest.players.forEach(player => {
      map[player.name] = { 
        score: player.sportSpecificData?.score ?? null 
        // finish and status not needed for completed view logic
      };
    });
    return map;
  }, [contest.players]);

  // Removed calculateRemainingPlayers and calculateIsEliminated

  // Calculate final team score
  const calculateFinalScore = useCallback((team) => {
    let totalScore = 0;
    let playersScoringCount = 0; 

    const captainStatus = playerDataMap[team.captain];
    if (captainStatus && typeof captainStatus.score === 'number') { 
      totalScore += captainStatus.score;
      playersScoringCount++;
    }

    team.flexPlayers.forEach(player => {
      const playerStatus = playerDataMap[player];
      if (playerStatus && typeof playerStatus.score === 'number') { 
        totalScore += playerStatus.score;
        playersScoringCount++;
      }
    });
    
    // Return null if no players scored, otherwise the score
    return playersScoringCount > 0 ? totalScore : null; 
  }, [playerDataMap]);

  // Helper function to calculate winnings based on rank and payouts
  const calculateWinnings = useCallback((rank, payouts, allEntrants) => {
    if (!payouts || payouts.length === 0) return 0;
    
    // Find all entrants with the same rank
    const tiedEntrants = allEntrants.filter(e => e.numericRank === rank);
    const numTied = tiedEntrants.length;
    
    // If no one is tied, return the payout directly
    if (numTied === 1) {
      return payouts[rank - 1] || 0;
    }
    
    // For ties, sum up the payouts for all tied positions and divide equally
    let totalPayout = 0;
    for (let i = 0; i < numTied; i++) {
      const payoutIndex = rank - 1 + i;
      if (payoutIndex < payouts.length) {
        totalPayout += payouts[payoutIndex];
      }
    }
    
    return Math.floor(totalPayout / numTied);
  }, []); // No dependencies needed now

  // Pre-process entrants: calculate rank with tiebreakers
  const processedEntrants = useMemo(() => {
    // 1. Map entrants to include final score AND captain's score
    const entrantsWithData = contest.entrants.map(entrant => {
      const captainName = entrant.team.captain;
      const captainData = playerDataMap[captainName];
      return {
        ...entrant,
        finalScore: calculateFinalScore(entrant.team),
        captainScore: captainData?.score ?? null,
        teamSalary: entrant.teamSalary
      };
    });

    // 2. Sort by final score (ASC - lower is better), then captain's score (ASC - lower is better)
    const sortedEntrants = [...entrantsWithData].sort((a, b) => {
      // Handle null final scores (push them to the end)
      if (a.finalScore === null && b.finalScore === null) return 0;
      if (a.finalScore === null) return 1;
      if (b.finalScore === null) return -1;
      
      const scoreDiff = a.finalScore - b.finalScore;
      if (scoreDiff !== 0) return scoreDiff;
      
      // Tiebreaker: captain's score (lower is better)
      if (a.captainScore === null && b.captainScore === null) return 0;
      if (a.captainScore === null) return 1;
      if (b.captainScore === null) return -1;
      return a.captainScore - b.captainScore;
    });

    // 3. Assign numeric rank based on sorted order, handling ties
    let currentRank = 0;
    let previousScore = null;
    let previousCaptainScore = null;
    const entrantsWithRank = sortedEntrants.map((entrant, index) => {
      if (entrant.finalScore !== previousScore || entrant.captainScore !== previousCaptainScore) {
        currentRank = index + 1;
        previousScore = entrant.finalScore;
        previousCaptainScore = entrant.captainScore;
      }
      return { ...entrant, rank: currentRank };
    });

    // 4. Determine display place (with "T" for ties)
    const entrantsWithPlace = entrantsWithRank.map((entrant, index, arr) => {
      const isTiedWithPrevious = index > 0 && entrant.rank === arr[index - 1].rank;
      const isTiedWithNext = index < arr.length - 1 && entrant.rank === arr[index + 1].rank;
      const placePrefix = isTiedWithPrevious || isTiedWithNext ? 'T' : '';
      return {
        ...entrant,
        place: `${placePrefix}${entrant.rank}`,
        numericRank: entrant.rank,
        displayName: entrant.entryName || entrant.username,
        teamSalary: entrant.teamSalary
      };
    });

    // 5. Add winnings after all other processing is done
    return entrantsWithPlace.map(entrant => ({
      ...entrant,
      winnings: calculateWinnings(entrant.rank, contest.entrantPayouts, entrantsWithPlace)
    }));

  }, [contest.entrants, playerDataMap, calculateFinalScore, calculateWinnings]);

  // Apply sorting hook (initial sort is by final rank/place)
  const { sortedData, requestSort, sortConfig } = useSortedData(
    processedEntrants,
    'numericRank', // Initial sort key is the numeric rank
    'ascending'    // Ranks go 1, 2, T3, T3, 5...
  );

  // Format score for display
  const formatScore = (score) => {
    if (score === null) return '-';
    if (score === 0) return 'E';
    return score > 0 ? `+${score}` : score.toString();
  };

  // Determine number of paying places
  const numPayingPlaces = contest.entrantPayouts?.length || 0;

  return (
    <div className="entrants-teams-container-golf-completed"> {/* UPDATED CLASS */} 
      <div className="entrants-teams-header">
        <span>Final Standings</span> {/* Updated Header Text */} 
        <span className="header-info-text">(Click team to view)</span>
      </div>
      <div className="entrants-teams-table-container">
        <table className="entrants-teams-table">
          <thead>
            <tr>
              {/* Order: Rank | User | Salary | Score | Winnings */}
              {/* Sort by numericRank when Rank header clicked */}
              <th onClick={() => requestSort('numericRank', true)}>Rank {getSortIndicator(sortConfig, 'numericRank')}</th> 
              <th onClick={() => requestSort('displayName')}>User {getSortIndicator(sortConfig, 'displayName')}</th>
              {/* Show Salary on Mobile */} 
              <th onClick={() => requestSort('teamSalary')}>Salary {getSortIndicator(sortConfig, 'teamSalary')}</th>
              {/* Removed Remaining Column */} 
              {/* Sort score ascending (lower is better) */}
              <th onClick={() => requestSort('finalScore', true)}>Score {getSortIndicator(sortConfig, 'finalScore')}</th> 
              <th onClick={() => requestSort('winnings')}>Won {getSortIndicator(sortConfig, 'winnings')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((entrant) => {
              const isCurrentUser = entrant.username?.toLowerCase() === currentUsername?.toLowerCase();
              // Removed teamStatusClass
              const userClass = isCurrentUser ? 'current-user-team' : '';
              // Use numericRank for money line check
              const isLastMoneyPlace = numPayingPlaces > 0 && entrant.numericRank === numPayingPlaces;
              const lastMoneyClass = isLastMoneyPlace ? 'last-money-row' : '';

              return (
                <tr
                  key={entrant._id}
                  onClick={() => onTeamSelect(entrant)}
                  // Removed teamStatusClass from className
                  className={`${userClass} ${lastMoneyClass}`.trim()}
                >
                  {/* Order: Rank | User | Salary | Score | Winnings */}
                  {/* Display the place string (e.g., "1", "T3") */}
                  <td>{entrant.place}</td> 
                  <td>
                    {entrant.displayName}
                  </td>
                  {/* Show Salary on Mobile */}
                  <td>${formatNumber(entrant.teamSalary)}</td>
                  {/* Removed Remaining Column */} 
                  {/* Display formatted score */}
                  <td>{formatScore(entrant.finalScore)}</td> 
                  <td>${formatNumber(entrant.winnings)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Updated PropTypes
EntrantsTeamsGolfCompleted.propTypes = {
  contest: PropTypes.shape({
    entrants: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
      username: PropTypes.string.isRequired,
      entryName: PropTypes.string,
      team: PropTypes.shape({
        captain: PropTypes.string.isRequired,
        flexPlayers: PropTypes.arrayOf(PropTypes.string).isRequired
      }).isRequired,
      teamSalary: PropTypes.number
    })).isRequired,
    players: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      sportSpecificData: PropTypes.shape({
        score: PropTypes.number // Need score for calculation
      })
      // finish, status, salary not directly needed in this completed view logic
    })).isRequired, 
    entrantPayouts: PropTypes.array, // Still used for money line
  }).isRequired,
  currentUsername: PropTypes.string, 
  onTeamSelect: PropTypes.func.isRequired
};

export default EntrantsTeamsGolfCompleted;
